'use client';

import Link from 'next/link';
import { useState, useRef } from 'react';
import EngagementTierCard, { type EngagementTier } from '@/components/EngagementTierCard';
import CTOOfficeInquiryForm from '@/components/CTOOfficeInquiryForm';

const ENGAGEMENT_TIERS: EngagementTier[] = [
  {
    name: 'Strategic Advisor',
    priceRange: '$2,500 - $4,000',
    period: 'month',
    description: 'Strategic guidance and advisory support for teams that need experienced input without heavy time commitment.',
    idealFor: 'Seed to Series A startups, non-technical founders, companies between CTO hires',
    features: [
      '2x 60-minute strategy calls per month',
      'Async access via dedicated channel (24-48hr response)',
      'Monthly written priorities brief',
      'Architecture review (up to 4 hours/month)',
      'Vendor and tool recommendations',
      'Interview participation (up to 2/month)',
      'Board/investor technical preparation',
    ],
  },
  {
    name: 'Engaged Partner',
    priceRange: '$6,000 - $10,000',
    period: 'month',
    description: 'Regular working sessions and hands-on involvement for teams scaling their engineering organization.',
    idealFor: 'Series A-B scale-ups, teams of 10-30 engineers, new CTOs needing mentorship',
    features: [
      'Weekly 90-minute working sessions',
      'Same-day async response during business hours',
      'Bi-weekly team meetings or standups',
      'Architecture review (up to 12 hours/month)',
      'Technology roadmap development',
      'Engineering process design',
      'Unlimited interview involvement',
      'CTO mentoring (if applicable)',
      'Quarterly team health assessments',
    ],
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    name: 'Embedded CTO',
    priceRange: '$15,000 - $25,000',
    period: 'month',
    description: 'Near full-time engagement with acting CTO responsibilities for companies in critical phases.',
    idealFor: 'Companies searching for permanent CTO, critical migrations, pre-acquisition preparation',
    features: [
      '2-3 days/week embedded with team',
      'Same-day/urgent access',
      'Full team integration',
      'Acting CTO responsibilities',
      'Full technology strategy ownership',
      'Team leadership and 1:1s',
      'Investor/board presentations',
      'Hiring and performance management',
      'Knowledge transfer to incoming CTO',
      'On-site presence available if needed',
    ],
  },
];

const PROBLEM_CARDS = [
  {
    icon: '📈',
    title: 'Outgrown Ad-Hoc Decisions',
    description: 'Your startup has outgrown ad-hoc technical decisions, but you are not ready for a $400K+ CTO hire.',
  },
  {
    icon: '🧭',
    title: 'Team Lacks Direction',
    description: 'Your engineering team is growing, but lacks strategic direction and experienced mentorship.',
  },
  {
    icon: '🎯',
    title: 'Need Proven Experience',
    description: 'You need someone who has been through scaling before and knows the pitfalls, patterns, and decisions that matter.',
  },
  {
    icon: '💰',
    title: 'CTO Expertise, Fraction of Cost',
    description: 'You need CTO-level guidance at 20% of the cost and 100% of the expertise.',
  },
];

const PROCESS_STEPS = [
  {
    step: 1,
    title: 'Discovery Conversation',
    description: 'Fill out the form below. We review every inquiry personally and respond within 1-2 business days.',
  },
  {
    step: 2,
    title: 'Custom Proposal',
    description: 'Within 48 hours of our first call, receive a tailored engagement recommendation with clear outcomes and pricing.',
  },
  {
    step: 3,
    title: 'Onboarding Sprint',
    description: 'Two-week deep dive into your codebase, team dynamics, and strategic context to get up to speed quickly.',
  },
  {
    step: 4,
    title: 'Ongoing Partnership',
    description: 'Regular cadence of calls, reviews, and guidance based on your chosen engagement tier.',
  },
];

const CLIENT_PROFILES = [
  {
    icon: '👨‍💻',
    title: 'The Technical Founder Stretched Thin',
    stage: 'Seed to Series A',
    challenge: 'You are writing code AND managing people AND talking to investors',
    need: 'Someone to share the technical load and provide strategic cover',
  },
  {
    icon: '👔',
    title: 'The Non-Technical CEO',
    stage: 'Series A to B',
    challenge: 'Your CTO left, or you never had one',
    need: 'Someone to translate tech to business and back',
  },
  {
    icon: '👥',
    title: 'The Growing Team',
    stage: '10-50 engineers',
    challenge: 'You have managers but no clear technical vision',
    need: 'Strategic guidance and process maturity',
  },
  {
    icon: '🔄',
    title: 'The Transition Company',
    stage: 'Pre-acquisition or major shift',
    challenge: 'Need credibility with buyers or partners',
    need: 'Experienced hands for due diligence or migration',
  },
];

const FAQ_ITEMS = [
  {
    question: 'How quickly can we start?',
    answer: 'After the discovery call and proposal, most engagements begin within 1-2 weeks. Embedded engagements may require a brief transition period for scheduling.',
  },
  {
    question: 'What is the minimum commitment?',
    answer: 'We recommend a 3-month initial engagement to see meaningful results. After that, month-to-month with 30 days notice.',
  },
  {
    question: 'Can I switch between tiers?',
    answer: 'Absolutely. As your needs change, we can adjust the engagement level up or down with 30 days notice.',
  },
  {
    question: 'Do you work with remote teams?',
    answer: 'Yes. Most engagements are fully remote. For Embedded tier, we can discuss on-site requirements if beneficial.',
  },
  {
    question: 'What if we hire a full-time CTO?',
    answer: 'That is the goal for many clients. We can help with the transition, knowledge transfer, and even stay on in an advisory capacity if helpful.',
  },
  {
    question: 'Do you do technical due diligence?',
    answer: 'Yes, this is a common add-on or primary engagement. We provide thorough technical assessments for M&A, investment, or internal audit purposes.',
  },
  {
    question: 'What industries do you work with?',
    answer: 'Primarily B2B SaaS, marketplaces, fintech, and developer tools. The principles of good engineering leadership translate across domains.',
  },
];

export default function CTOOfficePage() {
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const handleTierSelect = (tierName: string) => {
    setSelectedTier(tierName);
    // Scroll to form
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-light-surface to-white dark:from-background dark:to-surface py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          {/* Eyebrow */}
          <p className="text-accent font-semibold mb-4 tracking-wide uppercase text-sm">
            The CTO Office
          </p>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-bold text-light-text dark:text-foreground mb-6">
            Fractional CTO Services
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-light-text-secondary dark:text-foreground-secondary mb-8 max-w-3xl mx-auto">
            Experienced technical leadership for startups and scale-ups. Without the full-time commitment.
          </p>

          {/* CTA */}
          <button
            onClick={scrollToForm}
            className="bg-accent text-accent-foreground px-8 py-4 rounded-lg font-bold text-lg hover:bg-accent-hover transition-colors shadow-lg"
          >
            Start a Conversation
          </button>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 bg-white dark:bg-surface">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-4 text-center">
            The Technical Leadership Gap
          </h2>
          <p className="text-light-text-secondary dark:text-foreground-secondary text-center mb-12 max-w-2xl mx-auto">
            Most growing companies face a common challenge: they need CTO-level expertise but are not ready for a full-time executive hire.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROBLEM_CARDS.map((card, index) => (
              <div
                key={index}
                className="p-6 bg-light-surface dark:bg-background rounded-lg border border-light-border dark:border-border-muted"
              >
                <div className="text-4xl mb-4">{card.icon}</div>
                <h3 className="text-lg font-bold text-light-text dark:text-foreground mb-2">{card.title}</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-light-surface dark:bg-background">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-6">
            Battle-Tested Experience
          </h2>
          <p className="text-lg text-light-text-secondary dark:text-foreground-secondary mb-8">
            Drawing on two decades of building systems at scale, from early-stage startups to enterprise platforms, The CTO Office brings battle-tested experience to your technical challenges. No theoretical frameworks. No consultant-speak. Just practical guidance from someone who has been in the seat.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="p-4">
              <div className="text-4xl font-bold text-accent mb-1">20+</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-tertiary">Years Building at Scale</div>
            </div>
            <div className="p-4">
              <div className="text-4xl font-bold text-accent mb-1">100+</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-tertiary">Engineers Hired & Mentored</div>
            </div>
            <div className="p-4">
              <div className="text-4xl font-bold text-accent mb-1">5</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-tertiary">Successful Exits</div>
            </div>
            <div className="p-4">
              <div className="text-4xl font-bold text-accent mb-1">Millions</div>
              <div className="text-sm text-light-text-secondary dark:text-foreground-tertiary">Requests Handled Daily</div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Tiers */}
      <section className="py-16 bg-white dark:bg-surface">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-4 text-center">
            Engagement Tiers
          </h2>
          <p className="text-light-text-secondary dark:text-foreground-secondary text-center mb-12 max-w-2xl mx-auto">
            Choose the level of involvement that fits your needs. All engagements are customized based on your specific situation.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {ENGAGEMENT_TIERS.map((tier) => (
              <EngagementTierCard key={tier.name} tier={tier} onSelect={handleTierSelect} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-light-surface dark:bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-4 text-center">
            How It Works
          </h2>
          <p className="text-light-text-secondary dark:text-foreground-secondary text-center mb-12 max-w-2xl mx-auto">
            From first conversation to ongoing partnership in four steps.
          </p>

          <div className="grid md:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((step) => (
              <div key={step.step} className="text-center">
                <div className="w-12 h-12 bg-accent text-accent-foreground font-bold text-xl rounded-full flex items-center justify-center mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="font-bold text-light-text dark:text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 bg-white dark:bg-surface">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-4 text-center">
            Who This Is For
          </h2>
          <p className="text-light-text-secondary dark:text-foreground-secondary text-center mb-12 max-w-2xl mx-auto">
            Fractional CTO services work best for specific situations. See if your profile matches.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {CLIENT_PROFILES.map((profile, index) => (
              <div
                key={index}
                className="p-6 bg-light-surface dark:bg-background rounded-lg border border-light-border dark:border-border-muted"
              >
                <div className="flex items-start gap-4">
                  <div className="text-4xl">{profile.icon}</div>
                  <div>
                    <h3 className="text-lg font-bold text-light-text dark:text-foreground mb-1">{profile.title}</h3>
                    <p className="text-sm text-accent mb-2">{profile.stage}</p>
                    <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
                      <strong>Challenge:</strong> {profile.challenge}
                    </p>
                    <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                      <strong>Need:</strong> {profile.need}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-light-surface dark:bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-12 text-center">
            Questions & Answers
          </h2>

          <div className="space-y-6">
            {FAQ_ITEMS.map((item, index) => (
              <div
                key={index}
                className="bg-white dark:bg-surface border border-light-border dark:border-border rounded-lg p-6"
              >
                <h3 className="text-lg font-bold text-light-text dark:text-foreground mb-2">{item.question}</h3>
                <p className="text-light-text-secondary dark:text-foreground-secondary">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section ref={formRef} id="contact" className="py-16 bg-white dark:bg-surface">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-light-text dark:text-foreground mb-4 text-center">
            Ready to Talk?
          </h2>
          <p className="text-light-text-secondary dark:text-foreground-secondary text-center mb-8">
            Fill out the form below and we will get back to you within 1-2 business days with a custom proposal.
          </p>

          <div className="bg-light-surface dark:bg-background rounded-xl border border-light-border dark:border-border p-8">
            <CTOOfficeInquiryForm preselectedTier={selectedTier || undefined} />
          </div>

          {/* Alternative contact */}
          <p className="text-center text-sm text-light-text-tertiary dark:text-foreground-tertiary mt-6">
            Prefer email?{' '}
            <a href="mailto:hello@theartofcto.com" className="text-accent hover:underline">
              hello@theartofcto.com
            </a>
          </p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 bg-gradient-to-b from-light-surface to-white dark:from-background dark:to-surface">
        <div className="container mx-auto px-4 text-center">
          <p className="text-light-text-secondary dark:text-foreground-secondary mb-4">
            Part of{' '}
            <Link href="/" className="text-accent hover:underline font-semibold">
              The Art of CTO
            </Link>
            {' '}&mdash; Building systems. Leading people.
          </p>
        </div>
      </section>
    </main>
  );
}
